import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  DialogContentText
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ChangeEvent, useEffect, useState } from 'react';
import { http } from '../../api/http';

interface Slider {
  _id: string;
  title: string;
  imageUrl: string;
  order: number;
  isActive: boolean;
}

export const SliderPage = () => {
  const [sliders, setSliders] = useState<Slider[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Slider | null>(null);
  const [title, setTitle] = useState('');
  const [order, setOrder] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [deleteSlide, setDeleteSlide] = useState<Slider | null>(null);

  const fetchSliders = async () => {
    const res = await http.get<{ success: boolean; data: Slider[] }>('/sliders');
    setSliders(res.data.data);
  };

  useEffect(() => {
    void fetchSliders();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setTitle('');
    setOrder(0);
    setImageFile(null);
    setImagePreview('');
    setError(null);
    setOpen(true);
  };

  const openEdit = (s: Slider) => {
    setEditing(s);
    setTitle(s.title);
    setOrder(s.order);
    setImageFile(null);
    setImagePreview(s.imageUrl);
    setError(null);
    setOpen(true);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    try {
      setError(null);

      const formData = new FormData();
      formData.append('title', title);
      formData.append('order', String(order));
      if (imageFile) {
        formData.append('image', imageFile);
      }

      if (editing) {
        await http.put(`/sliders/${editing._id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await http.post('/sliders', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      setOpen(false);
      await fetchSliders();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save slide');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteSlide) return;
    try {
      await http.delete(`/sliders/${deleteSlide._id}`);
      setSliders((prev) => prev.filter((s) => s._id !== deleteSlide._id));
      setDeleteSlide(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete slide');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">Slider</Typography>
        <Button variant="contained" onClick={openCreate}>
          New Slide
        </Button>
      </Box>
      <Paper sx={{ p: 2 }}>
        <TableContainer>
          <Table size="medium">
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Image URL</TableCell>
                <TableCell>Order</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sliders.map((s) => (
                <TableRow
                  key={s._id}
                  hover
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell>{s.title}</TableCell>
                  <TableCell>{s.imageUrl}</TableCell>
                  <TableCell>{s.order}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => openEdit(s)} sx={{ mr: 0.5 }}>
                      Edit
                    </Button>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteSlide(s)}
                      aria-label="Delete"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Slide' : 'New Slide'}</DialogTitle>
        <DialogContent>
          <TextField
            margin="normal"
            fullWidth
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <Button
            variant="outlined"
            component="label"
            sx={{ mt: 2 }}
          >
            Choose Image
            <input type="file" hidden accept="image/*" onChange={handleFileChange} />
          </Button>
          {imagePreview && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" display="block">
                Preview:
              </Typography>
              <img
                src={imagePreview}
                alt="Preview"
                style={{ maxWidth: '100%', maxHeight: 150, borderRadius: 4 }}
              />
            </Box>
          )}
          <TextField
            margin="normal"
            fullWidth
            label="Order"
            type="number"
            value={order}
            onChange={(e) => setOrder(Number(e.target.value))}
          />
          {error && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteSlide} onClose={() => setDeleteSlide(null)}>
        <DialogTitle>Delete slide?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete &quot;{deleteSlide?.title}&quot;? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteSlide(null)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

